import { inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { catchError, filter, map, Observable, throwError } from 'rxjs';
import { HttpClient, HttpEventType, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { LoaderService } from './loader.service';
import { HttpOptions } from '../types/http-options';
import { AppErrorResponse, AppResponse } from '../types/http-response';

@Injectable({
    providedIn: 'root',
})
export class Http {
    private apiHost = `${environment.apiHost}/api/v1`;
    private _http = inject(HttpClient);
    private _router = inject(Router);
    private loaderService = inject(LoaderService);

    public get<T>(url: string, options?: HttpOptions): Observable<AppResponse<T>> {
        return this._http.get<AppResponse<T>>(`${this.apiHost}${url}`, options as any).pipe(
            catchError((error: AppErrorResponse) => {
                return this.handleError(error);
            }),
        ) as unknown as Observable<AppResponse<T>>;
    }

    public upload<T>(
        url: string,
        formData: FormData,
        options?: HttpOptions,
    ): Observable<AppResponse<T>> {
        const uploadOptions = {
            ...options,
            reportProgress: true,
            observe: 'events' as const,
        };
        return this._http
            .post<AppResponse<T>>(`${this.apiHost}${url}`, formData, uploadOptions as any)
            .pipe(
                filter(event => event.type === HttpEventType.Response),
                map((event: any) => event.body as AppResponse<T>),
                catchError((error: AppErrorResponse) => this.handleError(error)),
            );
    }

    public download(url: string, options?: HttpOptions): Observable<Blob> {
        const downloadOptions = {
            ...options,
            responseType: 'blob' as 'json',
            observe: 'response' as const,
        };
        return this._http
            .get(`${this.apiHost}${url}`, downloadOptions as any)
            .pipe(
                map((response: any) => response.body as Blob),
                catchError((error: AppErrorResponse) => this.handleError(error)),
            );
    }

    public post<T>(url: string, body: any, options?: HttpOptions): Observable<AppResponse<T>> {
        return this._http.post<AppResponse<T>>(`${this.apiHost}${url}`, body, options as any).pipe(
            catchError((error: AppErrorResponse) => {
                return this.handleError(error);
            }),
        ) as unknown as Observable<AppResponse<T>>;
    }

    public delete<T>(url: string, options?: HttpOptions): Observable<AppResponse<T>> {
        return this._http.delete<AppResponse<T>>(`${this.apiHost}${url}`, options as any).pipe(
            catchError((error: AppErrorResponse) => {
                return this.handleError(error);
            }),
        ) as unknown as Observable<AppResponse<T>>;
    }

    public put<T>(url: string, body: any, options?: HttpOptions): Observable<AppResponse<T>> {
        return this._http.put<AppResponse<T>>(`${this.apiHost}${url}`, body, options as any).pipe(
            catchError((error: AppErrorResponse) => {
                return this.handleError(error);
            }),
        ) as unknown as Observable<AppResponse<T>>;
    }

    public patch<T>(url: string, body: any, options?: HttpOptions): Observable<AppResponse<T>> {
        return this._http.patch<AppResponse<T>>(`${this.apiHost}${url}`, body, options as any).pipe(
            catchError((error: AppErrorResponse) => {
                return this.handleError(error);
            }),
        ) as unknown as Observable<AppResponse<T>>;
    }

    /**
     * Same as post() but adds X-Skip-Loader so the global spinner is suppressed.
     * Use for background / chunk-based calls that manage their own progress UI.
     */
    public postSilent<T>(url: string, body: any, options?: HttpOptions): Observable<AppResponse<T>> {
        const silentOptions = {
            ...options,
            headers: new HttpHeaders({ 'X-Skip-Loader': '1' }),
        };
        return this._http.post<AppResponse<T>>(`${this.apiHost}${url}`, body, silentOptions as any).pipe(
            catchError((error: AppErrorResponse) => {
                return this.handleError(error);
            }),
        ) as unknown as Observable<AppResponse<T>>;
    }

    private handleError(error: AppErrorResponse) {
        this.loaderService.hide();
        // Extract the most specific error message available from the backend envelope
        const backendMessage =
            typeof error.error === 'object'
                ? (error.error as any)?.message
                : error.message;
        return throwError(
            () => new Error(backendMessage || error.statusText || 'Unknown error occurred'),
        );
    }
}
